import { createEventReorderer } from "./eventReorderer";
import type {
  AnalysisChatV2Owner,
  AnalysisChatV2Route,
} from "@/app/api/analysis/chat/v2/route";
import { MissionControlStreamChatRoute } from "@/app/api/mission-control/stream/route";
import type { AnalysisChatEventEnvelope } from "@/backend/analysis-chat-v2/types";
import type { AgentQuestionAnswer } from "@/backend/db/drizzle/types/jsonTypes";
import type { ReportEditStreamRoute } from "@/backend/report-edit/types";
import { PageContext } from "@/components/unified-chat/core/types";
import { errorToMessage } from "@/lib/utils";
import { assertNever } from "@/packages/ts-utils";
import type {
  AnalysisChatV2FileRef,
  AnalysisChatV2Id,
  AnalysisChatV2MessageId,
  MissionControlChatId,
  MissionControlChatMessageId,
  OrganizationId,
  SurveyId,
} from "@/utils/types";

type RouteErrorPayload = {
  success: false;
  error: string;
  statusCode?: number;
};

const isRouteErrorPayload = (value: unknown): value is RouteErrorPayload => {
  if (!value || typeof value !== "object") {
    return false;
  }
  const record = value as { success?: unknown; error?: unknown };
  return record.success === false && typeof record.error === "string";
};

export class ChatStreamError extends Error {
  readonly status: number;

  constructor({ message, status }: { message: string; status: number }) {
    super(message);
    this.name = "ChatStreamError";
    this.status = status;
  }
}

export type SharedChatAccess = {
  sharingToken: string;
  chatSecret: string;
};

type AnalysisChatMessageConfig = {
  type: "analysis-chat-v2";
  chatId: AnalysisChatV2Id;
  messageId: AnalysisChatV2MessageId;
  owner: AnalysisChatV2Owner;
  clickedSuggestion?: boolean;
  pageContext?: PageContext;
  agentQuestionAnswers?: AgentQuestionAnswer[];
};

type MissionControlChatMessageConfig = {
  type: "mission-control-chat";
  chatId: MissionControlChatId;
  messageId: MissionControlChatMessageId;
  orgId: OrganizationId;
  surveyIds?: SurveyId[];
};

type ReportEditChatMessageConfig = {
  type: "report-edit";
  surveyId: SurveyId;
  reportId: ReportEditStreamRoute["input"]["reportId"];
  messageId: ReportEditStreamRoute["input"]["messageId"];
  currentReportItems: ReportEditStreamRoute["input"]["currentReportItems"];
  previousMessages?: ReportEditStreamRoute["input"]["previousMessages"];
  files?: ReportEditStreamRoute["input"]["files"];
  contextAttachments?: ReportEditStreamRoute["input"]["contextAttachments"];
};

export type StreamChatMessageConfig =
  | AnalysisChatMessageConfig
  | MissionControlChatMessageConfig
  | ReportEditChatMessageConfig;

export type FileRef = AnalysisChatV2FileRef;

type StreamChatMessageCommonArgs<TEnvelope> = {
  message: string;
  onEvent: (event: TEnvelope) => void;
  onStreamActivity?: () => void;
  onStarted?: () => void;
  signal?: AbortSignal;
  replayFrom?: number;
  replayOnly?: boolean;
  isNewChat?: boolean;
  files?: FileRef[];
};

type AnalysisStreamChatMessageArgs =
  StreamChatMessageCommonArgs<AnalysisChatEventEnvelope> &
    (AnalysisChatMessageConfig | MissionControlChatMessageConfig);
type ReportEditStreamChatMessageArgs =
  StreamChatMessageCommonArgs<AnalysisChatEventEnvelope> &
    ReportEditChatMessageConfig;
type StreamChatMessageArgs =
  | AnalysisStreamChatMessageArgs
  | ReportEditStreamChatMessageArgs;

type ChatEventEnvelope = {
  eventIndex: number;
  event: { type: string };
};

const readChatEventStream = async <TEnvelope extends ChatEventEnvelope>({
  response,
  onEvent,
  replayFrom,
  onStarted,
  onStreamActivity,
}: {
  response: Response;
  onEvent: (event: TEnvelope) => void;
  replayFrom?: number;
  onStarted?: () => void;
  onStreamActivity?: () => void;
}): Promise<void> => {
  if (!response.ok) {
    let errorMessage = "Failed to start chat. Please try again.";
    try {
      const payload = (await response.json()) as unknown;
      if (isRouteErrorPayload(payload)) {
        errorMessage = payload.error;
      }
    } catch {
      // ignore parse errors
    }
    throw new ChatStreamError({
      message: errorMessage,
      status: response.status,
    });
  }

  const body = response.body;
  if (!body) {
    throw new Error("Streaming response has no body");
  }
  onStarted?.();

  let receivedTerminalEvent = false;
  const reorderer = createEventReorderer<TEnvelope>({
    onEvent: (event) => {
      onEvent(event);
      if (event.event.type === "finish" || event.event.type === "error") {
        receivedTerminalEvent = true;
      }
    },
    startIndex: replayFrom ?? 0,
  });

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const processBuffer = () => {
    let boundary = buffer.indexOf("\n\n");
    while (boundary !== -1) {
      const rawEvent = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);

      const dataLines = rawEvent
        .split("\n")
        .filter((line) => line.startsWith("data:"));
      if (dataLines.length > 0) {
        const payload = dataLines
          .map((line) => line.slice("data:".length).trim())
          .join("");
        if (payload.length > 0) {
          try {
            const envelope = JSON.parse(payload) as TEnvelope;
            onStreamActivity?.();
            reorderer.push(envelope);
          } catch (error) {
            console.warn(
              "Failed to parse chat stream event payload",
              errorToMessage(error)
            );
          }
        }
      }

      boundary = buffer.indexOf("\n\n");
    }
  };

  try {
    let doneReading = false;
    while (!doneReading && !receivedTerminalEvent) {
      const { value, done } = await reader.read();
      doneReading = done;
      if (value) {
        buffer += decoder.decode(value, { stream: !done });
        processBuffer();
      }
    }
    if (!receivedTerminalEvent) {
      buffer += decoder.decode();
      processBuffer();
    }
  } finally {
    reader.releaseLock?.();
  }
};

export function streamChatMessage(
  args: AnalysisStreamChatMessageArgs
): Promise<void>;
export function streamChatMessage(
  args: ReportEditStreamChatMessageArgs
): Promise<void>;
export async function streamChatMessage(
  args: StreamChatMessageArgs
): Promise<void> {
  switch (args.type) {
    case "report-edit": {
      const requestBody: ReportEditStreamRoute["input"] = {
        surveyId: args.surveyId,
        reportId: args.reportId,
        messageId: args.messageId,
        message: args.message,
        currentReportItems: args.currentReportItems,
        previousMessages: args.previousMessages,
        contextAttachments: args.contextAttachments,
        ...(args.files != null &&
          args.files.length > 0 && {
            files: args.files,
          }),
        replayFrom: args.replayFrom,
        replayOnly: args.replayOnly,
      };
      const response = await fetch("/api/analysis/agentic/report/edit/stream", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        signal: args.signal,
      });
      await readChatEventStream<AnalysisChatEventEnvelope>({
        response,
        onEvent: args.onEvent,
        replayFrom: args.replayFrom,
        onStarted: args.onStarted,
        onStreamActivity: args.onStreamActivity,
      });
      return;
    }
    case "analysis-chat-v2": {
      const requestBody: AnalysisChatV2Route["input"] = {
        chatId: args.chatId,
        messageId: args.messageId,
        message: args.message,
        owner: args.owner,
        replayFrom: args.replayFrom,
        replayOnly: args.replayOnly,
        isNewChat: args.isNewChat,
        clickedSuggestion: args.clickedSuggestion,
        pageContext: args.pageContext,
        agentQuestionAnswers: args.agentQuestionAnswers,
        ...(args.files != null &&
          args.files.length > 0 && {
            files: args.files,
          }),
      };
      const response = await fetch("/api/analysis/chat/v2", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        signal: args.signal,
      });
      await readChatEventStream<AnalysisChatEventEnvelope>({
        response,
        onEvent: args.onEvent,
        replayFrom: args.replayFrom,
        onStarted: args.onStarted,
        onStreamActivity: args.onStreamActivity,
      });
      return;
    }
    case "mission-control-chat": {
      const requestBody: MissionControlStreamChatRoute["input"] = {
        chatId: args.chatId,
        messageId: args.messageId,
        message: args.message,
        orgId: args.orgId,
        replayOnly: args.replayOnly,
        replayFrom: args.replayFrom,
        isNewChat: args.isNewChat,
        surveyIds: args.surveyIds,
      };
      const response = await fetch("/api/mission-control/stream", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        signal: args.signal,
      });
      await readChatEventStream<AnalysisChatEventEnvelope>({
        response,
        onEvent: args.onEvent,
        replayFrom: args.replayFrom,
        onStarted: args.onStarted,
        onStreamActivity: args.onStreamActivity,
      });
      return;
    }
    default:
      assertNever(args);
  }
}
